/**
 * Best deal badge — gedeelde logica
 * ---------------------------------
 * Eén plek voor het BEST DEAL-label op de variant-pills. Wordt gebruikt door
 * de PDP-picker (variant-picker-dropdown.js), de quick view (smaak-dropdown.js
 * en de inline fallback in product-variant-picker-block.liquid), het
 * smaak-overlay op de PDP en de bundel-popup (product-bundle.js).
 *
 * Instelling per product: het metafield `custom.best_deal` (single line text),
 * dat in Liquid als `data-best-deal` op de picker komt.
 *   - leeg   → automatisch: de variant met de laagste prijs per gram/ml/stuk,
 *              binnen de huidige selectie (dus per smaak). Zet je een variant
 *              in de korting (bv. THT), dan springt het label vanzelf mee.
 *   - "uit"  → geen label op dit product.
 *   - "500g" → label altijd op die variant (vergelijkt tekst én hoeveelheid,
 *              dus "500", "500 g" en "0,5 kg" werken ook).
 *
 * Automatisch kijkt alleen naar leverbare varianten, en er moeten er minstens
 * twee te vergelijken zijn. Bij gelijke prijs per eenheid wint de grootste.
 * Smaak- en maatgroepen krijgen nooit een label.
 */
(function () {
  'use strict';
  if (window.cnBestDeal) return;

  var SKIP_KEYWORDS = [
    'smaak', 'flavour', 'flavor', 'geschmack', 'kleur', 'color', 'farbe',
    'maat', 'size', 'größe', 'grosse'
  ];

  // Omrekening naar een basiseenheid, zodat "1kg" en "500g" vergelijkbaar zijn.
  var UNITS = {
    g: ['mass', 1], gr: ['mass', 1], gram: ['mass', 1], grams: ['mass', 1],
    kg: ['mass', 1000], kilo: ['mass', 1000],
    ml: ['volume', 1], cl: ['volume', 10], l: ['volume', 1000], liter: ['volume', 1000], litre: ['volume', 1000]
  };

  function norm(s) {
    return String(s || '').trim().toLowerCase();
  }

  // "1000g" → { dim: 'mass', amount: 1000 }, "2,5 kg" → 2500 g,
  // "120 caps" → { dim: 'count', amount: 120 }. Null als er geen getal in staat.
  function parseAmount(text) {
    var m = String(text || '').match(/(\d+(?:[.,]\d+)*)\s*([a-zA-Z]*)/);
    if (!m) return null;
    var raw = m[1];
    // "1.000" / "1,000" met groepjes van drie = duizendtallen, anders decimaal.
    var num = /^\d{1,3}([.,]\d{3})+$/.test(raw)
      ? parseFloat(raw.replace(/[.,]/g, ''))
      : parseFloat(raw.replace(',', '.'));
    if (isNaN(num) || num <= 0) return null;
    var unit = UNITS[m[2].toLowerCase()];
    return unit ? { dim: unit[0], amount: num * unit[1] } : { dim: 'count', amount: num };
  }

  function skipGroup(name) {
    var n = norm(name);
    return SKIP_KEYWORDS.some(function (k) { return n.indexOf(k) !== -1; });
  }

  /**
   * Zet het label op `best` (null = op geen enkele pill). Raakt de DOM alleen
   * aan als de stand verandert. In de quick view roept een MutationObserver op
   * de hele pagina apply() bij elke wijziging opnieuw aan; toen het label
   * telkens werd weggehaald en teruggezet, hield dat de pagina eindeloos bezig
   * (gemeten op de whey-pagina: ~23 keer per seconde de hele pagina opnieuw
   * opmaken, 9 van de 12 seconden op een gewone telefoon).
   */
  function setBest(pills, best, label) {
    pills.forEach(function (p) {
      var badge = p.querySelector('.best-deal-badge');
      if (p === best) {
        if (!p.classList.contains('best-deal')) p.classList.add('best-deal');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'best-deal-badge';
          p.appendChild(badge);
        }
        if (badge.textContent !== label) badge.textContent = label;
      } else {
        if (p.classList.contains('best-deal')) p.classList.remove('best-deal');
        if (badge) badge.remove();
      }
    });
  }

  function matchesSetting(value, setting) {
    if (norm(value) === norm(setting)) return true;
    var a = parseAmount(value);
    var b = parseAmount(setting);
    return !!(a && b && a.dim === b.dim && a.amount === b.amount);
  }

  /**
   * Zet het label op de juiste pill in één optiegroep.
   *
   * opts:
   *   label       tekst van het label; leeg = functie uit
   *   setting     waarde van custom.best_deal ('' / 'uit' / '500g')
   *   groupName   naam van de optie (smaak/maat worden overgeslagen)
   *   variants    variant-objecten met price (centen), available, hideinshop
   *   pillValue   pill → optiewaarde
   *   valueOf     variant → waarde van deze optie
   *   inContext   variant → hoort bij de huidige overige selectie (bv. smaak)
   */
  function apply(pills, opts) {
    pills = Array.prototype.slice.call(pills || []);
    var label = String(opts.label || '').trim();
    var setting = String(opts.setting || '').trim();
    if (!label || norm(setting) === 'uit' || skipGroup(opts.groupName)) {
      setBest(pills, null, label);
      return;
    }

    var visible = pills.filter(function (p) { return p.style.display !== 'none'; });

    if (setting) {
      var forced = visible.find(function (p) { return matchesSetting(opts.pillValue(p), setting); });
      setBest(pills, forced || null, label);
      return;
    }

    var inContext = opts.inContext || function () { return true; };
    var candidates = [];
    visible.forEach(function (pill) {
      var value = opts.pillValue(pill);
      var amount = parseAmount(value);
      if (!amount) return;
      var variant = (opts.variants || []).find(function (v) {
        return v && v.hideinshop !== true && v.available && String(opts.valueOf(v)) === String(value) && inContext(v);
      });
      if (!variant || !(variant.price > 0)) return;
      candidates.push({ pill: pill, dim: amount.dim, amount: amount.amount, perUnit: variant.price / amount.amount });
    });

    // Alleen vergelijken met minstens twee kandidaten van dezelfde soort eenheid.
    if (candidates.length < 2 || candidates.some(function (c) { return c.dim !== candidates[0].dim; })) {
      setBest(pills, null, label);
      return;
    }

    var best = candidates.reduce(function (a, c) {
      var diff = c.perUnit - a.perUnit;
      // Kleine marge tegen afrondingsverschillen; bij gelijkspel wint de grootste.
      if (Math.abs(diff) < a.perUnit * 1e-6) return c.amount > a.amount ? c : a;
      return diff < 0 ? c : a;
    });
    setBest(pills, best.pill, label);
  }

  window.cnBestDeal = { apply: apply, skipGroup: skipGroup, parseAmount: parseAmount };
})();
